import { CompleteRequestStatus, MatchStatusType, useGetChattingRoomInfo } from "@src/hooks/auctionAgent/chat";

import { useCheckItemStatus, useConfirmMatch, useDenyCompleteMatchOnRequest } from "@src/hooks/auctionAgent/match";
import styled from "styled-components";
import MgDialog from "@src/components/Dialog";
import React, { useEffect, useState } from "react";
import { transferToNative } from "@src/lib/WebAppBridge";
import { useChattingRoomFromContext } from "@src/contexts/ChattingRoomContext";

import { MatchServiceType } from "@src/pages/auctionAgent/chatting";
import MatchCompleteNotification from "./MatchCompleteNotification";
import useSSEMatch from "@src/hooks/auctionAgent/sse";

import CompleteIcon from "./ui/MatchComplete.png";

type MatchUserButtonsProps = {
    matchRequestNo: number;
    chattingRoomNo: number;
};

export enum MatchUserDialogType {
    IDLE = 0,
    CONFIRM = 1,
    COMPLETE = 2,
    CHECK_ITEM_STATUS = 3,
    COMPLETE_ON_REQUEST = 4,
    COMPLETE_ON_REQUEST_BIDDING = 5,
}

const MatchUserButtons = ({ matchRequestNo, chattingRoomNo }: MatchUserButtonsProps) => {
    const { data: chattingRoomInfo, refetch: refetchChattingRoomInfo } = useGetChattingRoomInfo(chattingRoomNo, true);
    const { chattingRoomFromContext } = useChattingRoomFromContext();

    const { mutate: confirmMatch } = useConfirmMatch();
    const { completeRequestStatus: SseCompleteRequestStatus, takerCancelMatchRequestAt } = useSSEMatch();
    const { mutate: checkItemStatus } = useCheckItemStatus();
    const { mutate: denyCompleteMatchOnRequest } = useDenyCompleteMatchOnRequest();

    const [openDialog, setOpenDialog] = useState<MatchUserDialogType>(MatchUserDialogType.IDLE);
    const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);

    const [showConfirmSnackbar, setShowConfirmSnackbar] = useState(false);
    const [isHiding, setIsHiding] = useState(false);

    const isCompleteConsulting = !!chattingRoomInfo?.item.isCompleteConsulting;
    const handleOpenProfile = () => {
        if (chattingRoomInfo?.otherUser.userNo) {
            transferToNative(JSON.stringify({ userNo: chattingRoomInfo.otherUser.userNo }), "openProfile");
        }
    };

    const getMatchExpert = (serviceType: MatchServiceType) => {
        if (serviceType === MatchServiceType.경매대행) {
            return "[매물컨설팅 + 매수신청대리]";
        } else if (serviceType === MatchServiceType.매물컨설팅) {
            return "[매물컨설팅]";
        } else {
            return "[매수신청대리]";
        }
    };

    const handleMatchConfirm = () => {
        if (!chattingRoomFromContext) return;
        setOpenDialog(MatchUserDialogType.CONFIRM);
        setDialogContent(
            <ModalBox>
                <ModalTitle>매칭 확정</ModalTitle>
                <ModalContent>
                    {chattingRoomInfo?.otherUser.name} 전문가에게 <br />
                    {getMatchExpert(chattingRoomFromContext?.serviceType)} 서비스를 <br />
                    제공 받을까요?
                </ModalContent>
            </ModalBox>,
        );
    };

    const handleCompleteBidAgent = () => {
        if (!chattingRoomFromContext) return;
        setOpenDialog(MatchUserDialogType.COMPLETE);
        // 매물컨설팅 완료 상태
        setDialogContent(
            <ModalBox>
                <ModalTitle>매수신청대리 완료</ModalTitle>
                <Icon>
                    <img src={CompleteIcon} alt="complete" />
                </Icon>
                <ModalContent>
                    해당 {chattingRoomInfo?.item.itemSubId} 매물의 <br />
                    매수신청대리를 완료하였나요?
                    <LinkStyle
                        onClick={() => {
                            transferToNative("", "searchSoldItem");
                        }}
                    >
                        다른 매물로 진행했어요
                    </LinkStyle>
                </ModalContent>
            </ModalBox>,
        );
    };

    const handleCompleteConsulting = () => {
        if (!chattingRoomFromContext) return;
        setOpenDialog(MatchUserDialogType.COMPLETE);
        // 매물컨설팅 미완료 상태
        if (!isCompleteConsulting) {
            setDialogContent(
                <ModalBox>
                    <ModalTitle>매물컨설팅 완료</ModalTitle>
                    <Icon>
                        <img src={CompleteIcon} alt="complete" />
                    </Icon>
                    <ModalContent>
                        <div>
                            해당 {chattingRoomInfo?.item.itemSubId} 매물로 <br />
                            매물컨설팅을 완료하였나요?
                        </div>
                    </ModalContent>
                </ModalBox>,
            );
            return;
        }
    };

    useEffect(() => {
        if (showConfirmSnackbar) {
            const timer = setTimeout(() => {
                setIsHiding(true);
                setTimeout(() => {
                    setShowConfirmSnackbar(false);
                    setIsHiding(false);
                }, 300);
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [showConfirmSnackbar]);

    useEffect(() => {
        if (takerCancelMatchRequestAt) {
            transferToNative("", "closeWebView");
        }
    }, [takerCancelMatchRequestAt]);

    return (
        <>
            <MatchButtonBoxStyle>
                <FlexBox>
                    <button className="match-button" onClick={handleOpenProfile}>
                        전문가 프로필
                    </button>
                    {chattingRoomInfo?.matchStatus === MatchStatusType["매치신청"] && chattingRoomInfo?.matchRequestStatus !== 2 && (
                        <button className="match-button" onClick={handleMatchConfirm}>
                            매칭 확정
                        </button>
                    )}

                    {chattingRoomInfo?.matchStatus === MatchStatusType["진행중"] &&
                        chattingRoomFromContext?.serviceType === MatchServiceType.경매대행 && (
                            <button
                                className="match-button"
                                onClick={() => {
                                    if (!isCompleteConsulting) {
                                        if (chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기) {
                                            // 완료/거절 모달
                                            setOpenDialog(MatchUserDialogType.COMPLETE_ON_REQUEST);
                                        } else {
                                            // 매물컨설팅 완료 모달
                                            handleCompleteConsulting();
                                        }
                                    } else {
                                        // 매수신청대리 완료 모달
                                        if (chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기) {
                                            setOpenDialog(MatchUserDialogType.COMPLETE_ON_REQUEST_BIDDING);
                                        } else {
                                            handleCompleteBidAgent();
                                        }
                                    }
                                }}
                            >
                                {chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기
                                    ? "완료/거절하기"
                                    : "완료하기"}
                            </button>
                        )}

                    {chattingRoomInfo?.matchStatus === MatchStatusType["진행중"] &&
                        chattingRoomFromContext?.serviceType === MatchServiceType.매물컨설팅 && (
                            <button
                                className="match-button"
                                onClick={() => {
                                    if (chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기) {
                                        // 완료/거절 모달
                                        setOpenDialog(MatchUserDialogType.COMPLETE_ON_REQUEST);
                                    } else {
                                        // 매물컨설팅 완료 모달
                                        handleCompleteConsulting();
                                    }
                                }}
                            >
                                {chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기
                                    ? "완료/거절하기"
                                    : "완료하기"}
                            </button>
                        )}

                    {chattingRoomInfo?.matchStatus === MatchStatusType["진행중"] &&
                        chattingRoomFromContext?.serviceType === MatchServiceType.매수신청대리 && (
                            <button
                                className="match-button"
                                onClick={() => {
                                    // 매수신청대리 완료 모달
                                    if (chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기) {
                                        setOpenDialog(MatchUserDialogType.COMPLETE_ON_REQUEST_BIDDING);
                                    } else {
                                        handleCompleteBidAgent();
                                    }
                                }}
                            >
                                {chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기
                                    ? "완료/거절하기"
                                    : "완료하기"}
                            </button>
                        )}
                </FlexBox>
                {chattingRoomInfo?.item.completeRequestStatus === CompleteRequestStatus.완료대기 && (
                    <MatchCompleteNotification userType="user" />
                )}
            </MatchButtonBoxStyle>

            <MgDialog
                open={
                    openDialog === MatchUserDialogType.CONFIRM ||
                    openDialog === MatchUserDialogType.COMPLETE ||
                    openDialog === MatchUserDialogType.CHECK_ITEM_STATUS
                }
                onCloseView={() => {
                    setOpenDialog(MatchUserDialogType.IDLE);
                }}
                onClickSubmit={() => {
                    if (openDialog === MatchUserDialogType.CONFIRM) {
                        confirmMatch(
                            { matchRequestNo, isAccept: true },
                            {
                                onSuccess: () => {
                                    refetchChattingRoomInfo();
                                    setShowConfirmSnackbar(true);
                                },
                            },
                        );
                    } else if (openDialog === MatchUserDialogType.COMPLETE) {
                        // 매수신청대리 완료처리
                        if (isCompleteConsulting) {
                            // 매각결과 조회
                            checkItemStatus(chattingRoomInfo?.item.itemId, {
                                onSuccess: (data) => {
                                    // 매각된 경우에만 후기 작성
                                    if (data.status === "매각") {
                                        transferToNative(
                                            JSON.stringify({ matchRequestNo, serviceType: chattingRoomFromContext!.serviceType }),
                                            "createReview",
                                        );
                                    } else {
                                        // 입찰결과 확인 모달 띄우기
                                        setOpenDialog(MatchUserDialogType.CHECK_ITEM_STATUS);
                                        setDialogContent(
                                            <ModalBox>
                                                <ModalTitle>입찰결과 확인</ModalTitle>
                                                <ModalContent>
                                                    해당 {chattingRoomInfo?.item.itemSubId} <br />
                                                    아직 경매가 진행중이므로 <br />
                                                    대행 완료가 불가합니다.
                                                </ModalContent>
                                            </ModalBox>,
                                        );
                                    }
                                },
                            });
                            return;
                        }

                        // 매물컨설팅 완료처리
                        if (chattingRoomInfo?.matchRequestNo) {
                            transferToNative(
                                JSON.stringify({ matchRequestNo, serviceType: chattingRoomFromContext!.serviceType }),
                                "createReview",
                            );
                        }
                    }
                    setOpenDialog(MatchUserDialogType.IDLE);
                }}
                width="312px"
                cancelText={openDialog === MatchUserDialogType.CHECK_ITEM_STATUS ? undefined : "아니요"}
                confirmText="네"
                confirmStyle={{ fontSize: "18px" }}
                cancelStyle={{ fontSize: "18px" }}
            >
                {dialogContent}
            </MgDialog>

            <MgDialog
                open={openDialog === MatchUserDialogType.COMPLETE_ON_REQUEST}
                onCloseView={() => {
                    //  거절하기
                    if (!chattingRoomInfo?.item.matchNo) return;
                    denyCompleteMatchOnRequest(chattingRoomInfo?.item.matchNo, {
                        onSuccess: () => {
                            refetchChattingRoomInfo();
                        },
                    });
                    setOpenDialog(MatchUserDialogType.IDLE);
                }}
                onClickSubmit={() => {
                    // 완료하기
                    handleCompleteConsulting();
                }}
                width="312px"
                cancelText={"거절하기"}
                confirmText="완료하기"
                confirmStyle={{ fontSize: "18px" }}
                cancelStyle={{ fontSize: "18px" }}
            >
                <ModalBox>
                    <ModalTitle>매물컨설팅 완료</ModalTitle>
                    <ModalContent>
                        전문가가 완료 요청을 보냈어요. <br />
                        완료하시겠습니까?
                    </ModalContent>
                </ModalBox>
            </MgDialog>

            <MgDialog
                open={openDialog === MatchUserDialogType.COMPLETE_ON_REQUEST_BIDDING}
                onCloseView={() => {
                    // 거절하기
                    if (!chattingRoomInfo?.item.matchNo) return;
                    denyCompleteMatchOnRequest(chattingRoomInfo?.item.matchNo, {
                        onSuccess: () => {
                            refetchChattingRoomInfo();
                        },
                    });
                    setOpenDialog(MatchUserDialogType.IDLE);
                }}
                onClickSubmit={() => {
                    // 완료하기
                    handleCompleteBidAgent();
                }}
                width="312px"
                cancelText={"거절하기"}
                confirmText="완료하기"
                confirmStyle={{ fontSize: "18px" }}
                cancelStyle={{ fontSize: "18px" }}
            >
                <ModalBox>
                    <ModalTitle>매수신청대리 완료 요청</ModalTitle>
                    <ModalContent>
                        전문가가 완료 요청을 보냈어요.
                        <br />
                        완료를 진행하시겠습니까?
                        <br />
                    </ModalContent>
                </ModalBox>
            </MgDialog>

            {showConfirmSnackbar && (
                <SnackbarStyle className={isHiding ? "hide" : ""}>
                    {chattingRoomInfo?.otherUser.name}님이 담당 매수대리인으로 매칭되었어요!
                </SnackbarStyle>
            )}
        </>
    );
};

export default MatchUserButtons;

const ModalBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: center;
    justify-content: center;
`;

const ModalTitle = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: #0c002c;
`;

const Icon = styled.div`
    width: 240px;
    height: 240px;
    img {
        width: 100%;
        height: 100%;
    }
`;

const ModalContent = styled.div`
    font-size: 16px;
    font-weight: 400;
    color: #0c002c;
    text-align: center;
    line-height: 1.5;
    margin-bottom: 12px;
`;

const LinkStyle = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: #737177;
    text-align: center;
    line-height: 1.5;
    margin-top: 12px;

    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 3px;
`;

const SnackbarStyle = styled.div`
    position: fixed;
    bottom: 68px;
    left: 0;
    width: 100%;
    padding: 14px 0;
    background-color: #dff0ff;
    color: #2275ee;
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    opacity: 1;
    transform: translateY(0);
    transition: all 0.3s ease-in-out;

    &.hide {
        opacity: 0;
        transform: translateY(20px);
    }
`;

const MatchButtonBoxStyle = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
`;

const FlexBox = styled.div`
    display: flex;
    gap: 12px;
`;
